import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useState } from 'react';

import { useDeleteBaermann } from '@/features/analisis/baermann/hooks/useBaermann';
import { useDeleteCoprocultivo } from '@/features/analisis/coprocultivo/hooks/useCoprocultivo';
import { useDeleteEctoparasitos } from '@/features/analisis/ectoparasitos/hooks/useEctoparasitos';
import { useDeleteHpg } from '@/features/analisis/hpg/hooks/useHpg';
import { useDeleteSedimentacionFh } from '@/features/analisis/sedimentacion-fh/hooks/useSedimentacionFh';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage } from '@/lib/apiError';
import { TipoAnalisis } from '@/types/enums';
import { formatDateAR } from '@/utils/formatters';

import type { GrupoAnalisis } from '../grupoAnalisis';

interface EliminarAnalisisDialogProps {
  open: boolean;
  uidProtocolo: string;
  grupo: GrupoAnalisis | null;
  onCancel: () => void;
  onSuccess: () => void;
}

export function EliminarAnalisisDialog({
  open,
  uidProtocolo,
  grupo,
  onCancel,
  onSuccess,
}: EliminarAnalisisDialogProps): JSX.Element {
  const notify = useNotify();
  const [isPending, setIsPending] = useState(false);
  const [mutationError, setMutationError] = useState<unknown>(null);

  const deleteHpg = useDeleteHpg();
  const deleteCoprocultivo = useDeleteCoprocultivo();
  const deleteBaermann = useDeleteBaermann();
  const deleteSedFh = useDeleteSedimentacionFh();
  const deleteEcto = useDeleteEctoparasitos();

  async function handleConfirm(): Promise<void> {
    if (!grupo) return;
    setIsPending(true);
    setMutationError(null);
    try {
      if (grupo.tipoGrupo === 'hpg') {
        const cascade = grupo.complementarios.length > 0;
        await deleteHpg.mutateAsync({
          uidProtocolo,
          uidAnalisis: grupo.hpg.uid_analisis,
          cascade,
        });
      } else {
        const uid = grupo.analisis.uid_analisis;
        const params = { uidProtocolo, uidAnalisis: uid };
        switch (grupo.tipo) {
          case TipoAnalisis.COPROCULTIVO:
            await deleteCoprocultivo.mutateAsync(params);
            break;
          case TipoAnalisis.BAERMANN:
            await deleteBaermann.mutateAsync(params);
            break;
          case TipoAnalisis.SEDIMENTACION_FH:
            await deleteSedFh.mutateAsync(params);
            break;
          case TipoAnalisis.ECTOPARASITOS:
            await deleteEcto.mutateAsync(params);
            break;
        }
      }
      notify.success('Análisis eliminado');
      onSuccess();
    } catch (err) {
      setMutationError(err);
    } finally {
      setIsPending(false);
    }
  }

  function handleCancel(): void {
    setMutationError(null);
    onCancel();
  }

  let textoConfirmacion = '';
  if (grupo) {
    if (grupo.tipoGrupo === 'hpg') {
      const fechaStr = formatDateAR(grupo.hpg.fecha_creacion);
      const n = grupo.complementarios.length;
      if (n > 0) {
        textoConfirmacion = `Vas a eliminar el análisis HPG y sus ${n} complementario${n !== 1 ? 's' : ''}. Esta acción es irreversible. ¿Confirmás?`;
      } else {
        textoConfirmacion = `Vas a eliminar el análisis HPG cargado el ${fechaStr}. Esta acción es irreversible. ¿Confirmás?`;
      }
    } else {
      const NOMBRE: Record<string, string> = {
        [TipoAnalisis.COPROCULTIVO]: 'Coprocultivo',
        [TipoAnalisis.BAERMANN]: 'Baermann',
        [TipoAnalisis.SEDIMENTACION_FH]: 'Sedimentación Fh',
        [TipoAnalisis.ECTOPARASITOS]: 'Ectoparásitos',
      };
      const nombre = NOMBRE[grupo.tipo] ?? grupo.tipo;
      const fechaStr = formatDateAR(grupo.analisis.fecha_creacion);
      textoConfirmacion = `Vas a eliminar el análisis de ${nombre} cargado el ${fechaStr}. Esta acción es irreversible. ¿Confirmás?`;
    }
  }

  return (
    <Dialog open={open} onClose={isPending ? undefined : handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Eliminar análisis</DialogTitle>
      <DialogContent>
        {!!mutationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getErrorMessage(mutationError)}
          </Alert>
        )}
        <DialogContentText>{textoConfirmacion}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isPending}>
          Cancelar
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          color="error"
          variant="contained"
          disabled={isPending}
        >
          {isPending ? 'Eliminando...' : 'Eliminar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
